import { useState, useMemo, useRef, useEffect } from 'react';
import { useCollection } from '../../../hooks/useCollection';
import { supabase, uploadToStorage } from '../../../config/supabase';
import {
  FIELDS, RISK_LEVELS, STATUSES, emptyRecord, fullName, isSamePerson,
  parseImportFile, validateRecord, exportExcel, exportCsv, downloadTemplate, logAudit,
} from '../utils/blacklistService';

const riskColor = (l) => ({ critical: '#7f1d1d', high: '#dc2626', medium: '#f59e0b', low: '#64748b' }[String(l || '').toLowerCase()] || '#64748b');

/**
 * Super-Admin Blacklist manager: list, search, filters, CRUD, attachments,
 * import (xlsx/csv), export (xlsx/csv/pdf), audit log. Global (bypassAgencyFilter).
 */
export default function BlacklistManager({ T = {}, dm = false, saActor = 'super-admin' }) {
  const { data: list, add, update, remove } = useCollection('blacklist', { bypassAgencyFilter: true });

  const [tab, setTab] = useState('list'); // list | audit
  const [search, setSearch] = useState('');
  const [fCity, setFCity] = useState('');
  const [fCountry, setFCountry] = useState('');
  const [fRisk, setFRisk] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fAgency, setFAgency] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null); // record being edited/created
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [importPreview, setImportPreview] = useState(null); // { records, dups, errors }
  const [dragOver, setDragOver] = useState(false);
  const [audit, setAudit] = useState([]);
  const fileRef = useRef(null);

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  // ── Filtering / search ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(r => {
      if (fCity && (r.city || '').toLowerCase() !== fCity.toLowerCase()) return false;
      if (fCountry && (r.country || '').toLowerCase() !== fCountry.toLowerCase()) return false;
      if (fRisk && (r.riskLevel || '') !== fRisk) return false;
      if (fStatus && (r.status || '') !== fStatus) return false;
      if (fAgency && !(r.sourceAgency || '').toLowerCase().includes(fAgency.toLowerCase())) return false;
      if (fFrom && r.created_at && new Date(r.created_at) < new Date(fFrom)) return false;
      if (fTo && r.created_at && new Date(r.created_at) > new Date(fTo + 'T23:59:59')) return false;
      if (q) {
        const hay = [r.firstName, r.lastName, fullName(r), r.cnie, r.passport, r.license, r.phone, r.phone2, r.email, r.city, r.address, r.sourceAgency]
          .map(x => (x || '').toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list, search, fCity, fCountry, fRisk, fStatus, fAgency, fFrom, fTo]);

  const cities = useMemo(() => [...new Set(list.map(r => r.city).filter(Boolean))].sort(), [list]);
  const agencies = useMemo(() => [...new Set(list.map(r => r.sourceAgency).filter(Boolean))].sort(), [list]);

  // ── Save (add / edit) ──
  const saveRecord = async () => {
    const errs = validateRecord(editing);
    if (errs.length) { notify('❌ ' + errs.join(' · ')); return; }
    setBusy(true);
    try {
      const rec = { ...editing, fullName: fullName(editing), updatedAt: new Date().toISOString() };
      if (editing.id) {
        await update(editing.id, rec);
        await logAudit('edited', { actor: saActor, targetId: editing.id, targetName: fullName(editing) });
        notify('✅ Enregistré');
      } else {
        rec.addedAt = new Date().toISOString();
        rec.addedBy = saActor;
        const ins = await add(rec);
        await logAudit('added', { actor: saActor, targetId: ins?.id, targetName: fullName(editing) });
        notify('✅ Ajouté à la liste noire');
      }
      setEditing(null);
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const del = async (r) => {
    if (!confirm(`Supprimer ${fullName(r)} de la liste noire ?`)) return;
    await remove(r.id);
    await logAudit('deleted', { actor: saActor, targetId: r.id, targetName: fullName(r) });
    notify('🗑️ Supprimé');
  };

  const toggleStatus = async (r) => {
    const status = (r.status === 'Active') ? 'Inactive' : 'Active';
    await update(r.id, { status });
    await logAudit(status === 'Active' ? 'approval' : 'rejection', { actor: saActor, targetId: r.id, targetName: fullName(r), details: { status } });
  };

  // ── Attachments ──
  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const atts = [...(editing.attachments || [])];
    for (const f of files) {
      try {
        const url = await uploadToStorage(f, 'blacklist');
        atts.push({ name: f.name, url, type: f.type, size: f.size });
      } catch (err) { notify('❌ Upload: ' + err.message); }
    }
    setEditing({ ...editing, attachments: atts });
    setBusy(false);
  };

  // ── Import ──
  const handleImportFile = async (file) => {
    if (!file) return;
    if (/\.pdf$/i.test(file.name)) {
      notify('ℹ️ PDF: extraction automatique non fiable — utilisez Excel/CSV. (À venir)');
      return;
    }
    try {
      const records = await parseImportFile(file);
      const dups = [];
      const errors = [];
      records.forEach((rec, i) => {
        const e = validateRecord(rec);
        if (e.length) errors.push({ row: i + 2, errs: e, rec });
        if (list.some(x => isSamePerson(rec, x))) dups.push(i);
      });
      setImportPreview({ records, dups, errors, skipDups: true });
    } catch (e) { notify('❌ Import: ' + e.message); }
  };

  const runImport = async () => {
    if (!importPreview) return;
    setBusy(true);
    let added = 0, updated = 0, skipped = 0;
    try {
      for (let i = 0; i < importPreview.records.length; i++) {
        const rec = importPreview.records[i];
        if (validateRecord(rec).length) { skipped++; continue; }
        const existing = list.find(x => isSamePerson(rec, x));
        if (existing) {
          if (importPreview.skipDups) { skipped++; continue; }
          await update(existing.id, { ...rec, fullName: fullName(rec), updatedAt: new Date().toISOString() });
          updated++;
        } else {
          await add({ ...rec, fullName: fullName(rec), addedBy: saActor, addedAt: new Date().toISOString() });
          added++;
        }
      }
      await logAudit('imported', { actor: saActor, details: { added, updated, skipped, total: importPreview.records.length } });
      notify(`✅ Import: ${added} ajoutés, ${updated} mis à jour, ${skipped} ignorés`);
      setImportPreview(null);
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  // ── Export ──
  const exportRecords = (fmt) => {
    const recs = selected.size ? filtered.filter(r => selected.has(r.id)) : filtered;
    if (fmt === 'xlsx') exportExcel(recs);
    else if (fmt === 'csv') exportCsv(recs);
    else if (fmt === 'pdf') exportPdf(recs);
    logAudit('exported', { actor: saActor, details: { format: fmt, count: recs.length } });
  };

  const exportPdf = (recs) => {
    const rows = recs.map(r => `<tr><td>${fullName(r)}</td><td>${r.cnie || ''}</td><td>${r.phone || ''}</td><td>${r.city || ''}</td><td>${r.riskLevel || ''}</td><td>${r.status || ''}</td><td>${(r.reason || '').replace(/</g, '&lt;')}</td></tr>`).join('');
    const html = `<html><head><meta charset="utf-8"><title>Liste noire</title><style>body{font-family:system-ui;padding:20px}h1{color:#dc2626}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ccc;padding:6px;text-align:left}th{background:#fee2e2}</style></head><body><h1>⚠️ Liste noire globale (${recs.length})</h1><table><thead><tr><th>Nom</th><th>C.N.I.E</th><th>Tél</th><th>Ville</th><th>Risque</th><th>Statut</th><th>Motif</th></tr></thead><tbody>${rows}</tbody></table><script>window.onload=()=>window.print()</script></body></html>`;
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); }
  };

  // ── Audit log loader ──
  useEffect(() => {
    if (tab !== 'audit') return;
    supabase.from('blacklist_audit').select('*').order('ts', { ascending: false }).limit(200)
      .then(({ data }) => setAudit(data || []));
  }, [tab]);

  const inp = (val, on, ph, type = 'text') => (
    <input type={type} value={val || ''} placeholder={ph} onChange={e => on(e.target.value)}
      style={{ width: '100%', padding: '9px 11px', borderRadius: '8px', border: `1px solid ${T.cardBorder || '#e2e8f0'}`, background: T.card || '#fff', color: T.text || '#111', fontSize: '13px', boxSizing: 'border-box' }} />
  );

  // ════════════════════════════════════════════════════════════════════
  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>🚫 Liste noire globale</h2>
          <div style={{ fontSize: '13px', color: T.textMuted }}>Partagée avec toutes les agences · vérification automatique des contrats.</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['list', 'audit'].map(k => (
            <button key={k} onClick={() => setTab(k)} style={{ padding: '7px 14px', borderRadius: '9px', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer', background: tab === k ? '#6366f1' : (dm ? '#1e293b' : '#e2e8f0'), color: tab === k ? '#fff' : T.textSub }}>
              {k === 'list' ? `📋 Liste (${list.length})` : '🧾 Audit'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'list' && <>
        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <button onClick={() => setEditing(emptyRecord())} style={{ padding: '9px 14px', borderRadius: '9px', background: '#dc2626', color: '#fff', border: 'none', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}>➕ Ajouter</button>
          <button onClick={() => fileRef.current?.click()} style={{ padding: '9px 14px', borderRadius: '9px', background: '#16a34a', color: '#fff', border: 'none', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}>📥 Importer</button>
          <button onClick={downloadTemplate} style={{ padding: '9px 14px', borderRadius: '9px', background: dm ? '#1e293b' : '#e2e8f0', color: T.text, border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>📄 Modèle</button>
          <button onClick={() => exportRecords('xlsx')} style={{ padding: '9px 14px', borderRadius: '9px', background: '#059669', color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>⬇️ Excel</button>
          <button onClick={() => exportRecords('csv')} style={{ padding: '9px 14px', borderRadius: '9px', background: '#0284c7', color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>⬇️ CSV</button>
          <button onClick={() => exportRecords('pdf')} style={{ padding: '9px 14px', borderRadius: '9px', background: '#b91c1c', color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>⬇️ PDF</button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" style={{ display: 'none' }} onChange={e => handleImportFile(e.target.files?.[0])} />
        </div>

        {/* Drag & drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleImportFile(e.dataTransfer.files?.[0]); }}
          style={{ border: `2px dashed ${dragOver ? '#16a34a' : (T.cardBorder || '#cbd5e1')}`, borderRadius: '12px', padding: '10px', textAlign: 'center', color: T.textSub, fontSize: '12px', marginBottom: '12px', background: dragOver ? 'rgba(22,163,74,0.08)' : 'transparent' }}>
          📎 Glissez-déposez un fichier Excel / CSV ici pour importer en masse
        </div>

        {/* Search + filters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '8px', marginBottom: '12px' }}>
          {inp(search, setSearch, '🔎 Rechercher (nom, CNIE, tél, email, ville...)')}
          <select value={fRisk} onChange={e => setFRisk(e.target.value)} style={{ padding: '9px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }}><option value="">Risque (tous)</option>{RISK_LEVELS.map(r => <option key={r}>{r}</option>)}</select>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ padding: '9px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }}><option value="">Statut (tous)</option>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          <select value={fCity} onChange={e => setFCity(e.target.value)} style={{ padding: '9px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }}><option value="">Ville (toutes)</option>{cities.map(c => <option key={c}>{c}</option>)}</select>
          <select value={fAgency} onChange={e => setFAgency(e.target.value)} style={{ padding: '9px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }}><option value="">Agence (toutes)</option>{agencies.map(a => <option key={a}>{a}</option>)}</select>
          {inp(fFrom, setFFrom, 'Du', 'date')}
          {inp(fTo, setFTo, 'Au', 'date')}
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
            <thead>
              <tr style={{ background: dm ? '#1e293b' : '#f8fafc', color: T.textSub, textAlign: 'left' }}>
                <th style={{ padding: '9px' }}></th>
                {['Nom complet', 'C.N.I.E', 'Tél', 'Ville', 'Risque', 'Statut', 'Agence', 'Actions'].map(h => <th key={h} style={{ padding: '9px', fontWeight: 800 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '18px', textAlign: 'center', color: T.textSub }}>Aucun enregistrement.</td></tr>
              ) : filtered.map(r => (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.cardBorder}`, color: T.text }}>
                  <td style={{ padding: '9px' }}><input type="checkbox" checked={selected.has(r.id)} onChange={() => { const n = new Set(selected); n.has(r.id) ? n.delete(r.id) : n.add(r.id); setSelected(n); }} /></td>
                  <td style={{ padding: '9px', fontWeight: 700 }}>{fullName(r) || '—'}</td>
                  <td style={{ padding: '9px' }}>{r.cnie || '—'}</td>
                  <td style={{ padding: '9px' }}>{r.phone || '—'}</td>
                  <td style={{ padding: '9px' }}>{r.city || '—'}</td>
                  <td style={{ padding: '9px' }}><span style={{ background: riskColor(r.riskLevel), color: '#fff', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 800 }}>{r.riskLevel || '—'}</span></td>
                  <td style={{ padding: '9px' }}><button onClick={() => toggleStatus(r)} style={{ cursor: 'pointer', border: 'none', borderRadius: '999px', padding: '2px 8px', fontSize: '11px', fontWeight: 800, background: r.status === 'Active' ? '#dcfce7' : '#f1f5f9', color: r.status === 'Active' ? '#15803d' : '#64748b' }}>{r.status || 'Active'}</button></td>
                  <td style={{ padding: '9px' }}>{r.sourceAgency || '—'}</td>
                  <td style={{ padding: '9px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditing({ ...emptyRecord(), ...r })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px' }}>✏️</button>
                    <button onClick={() => del(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px' }}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '11px', color: T.textSub, marginTop: '6px' }}>{filtered.length} résultat(s){selected.size ? ` · ${selected.size} sélectionné(s)` : ''}</div>
      </>}

      {tab === 'audit' && (
        <div style={{ overflowX: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr style={{ background: dm ? '#1e293b' : '#f8fafc', color: T.textSub, textAlign: 'left' }}>{['Date', 'Action', 'Utilisateur', 'Agence', 'Cible', 'IP', 'Appareil'].map(h => <th key={h} style={{ padding: '9px', fontWeight: 800 }}>{h}</th>)}</tr></thead>
            <tbody>
              {audit.length === 0 ? <tr><td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: T.textSub }}>Aucune activité.</td></tr>
                : audit.map(a => (
                  <tr key={a.id} style={{ borderTop: `1px solid ${T.cardBorder}`, color: T.text }}>
                    <td style={{ padding: '8px' }}>{new Date(a.ts).toLocaleString('fr-FR')}</td>
                    <td style={{ padding: '8px', fontWeight: 700 }}>{a.action}</td>
                    <td style={{ padding: '8px' }}>{a.actor || '—'}</td>
                    <td style={{ padding: '8px' }}>{a.agency_id || '—'}</td>
                    <td style={{ padding: '8px' }}>{a.target_name || '—'}</td>
                    <td style={{ padding: '8px' }}>{a.ip || '—'}</td>
                    <td style={{ padding: '8px' }}>{a.details?.browser || ''} {a.details?.device || ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / add modal */}
      {editing && (
        <div onClick={e => e.target === e.currentTarget && setEditing(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: T.card || '#fff', borderRadius: '16px', width: '100%', maxWidth: '720px', maxHeight: '92vh', overflowY: 'auto', padding: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontWeight: 900, color: T.text }}>{editing.id ? '✏️ Modifier' : '➕ Ajouter'} — {fullName(editing) || 'Nouvelle personne'}</h3>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: T.textSub }}>×</button>
            </div>
            {['Personnel', 'Identification', 'Contact', 'Adresse', 'Location', 'Admin'].map(group => (
              <div key={group} style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: T.textSub, margin: '6px 0' }}>{group}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '8px' }}>
                  {FIELDS.filter(f => f.group === group).map(f => {
                    if (f.key === 'riskLevel') return <select key={f.key} value={editing.riskLevel} onChange={e => setEditing({ ...editing, riskLevel: e.target.value })} style={{ padding: '9px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }}>{RISK_LEVELS.map(r => <option key={r}>{r}</option>)}</select>;
                    if (f.key === 'status') return <select key={f.key} value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} style={{ padding: '9px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>;
                    return <div key={f.key}><label style={{ fontSize: '10px', color: T.textSub, fontWeight: 700 }}>{f.label}</label>{inp(editing[f.key], v => setEditing({ ...editing, [f.key]: v }), f.label, f.type || 'text')}</div>;
                  })}
                </div>
              </div>
            ))}
            {/* Attachments */}
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: T.textSub, margin: '6px 0' }}>📎 Pièces jointes (PDF, images, Word, Excel, ZIP)</div>
              <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,image/*" onChange={onAttach} style={{ fontSize: '12px', color: T.text }} />
              <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(editing.attachments || []).map((a, i) => (
                  <span key={i} style={{ fontSize: '11px', background: dm ? '#1e293b' : '#f1f5f9', borderRadius: '8px', padding: '3px 8px', color: T.text }}>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{a.name}</a>
                    <button onClick={() => setEditing({ ...editing, attachments: editing.attachments.filter((_, j) => j !== i) })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', marginLeft: '4px' }}>×</button>
                  </span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setEditing(null)} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: dm ? '#1e293b' : '#e2e8f0', color: T.text, fontWeight: 700, cursor: 'pointer' }}>Annuler</button>
              <button onClick={saveRecord} disabled={busy} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>{busy ? '⏳' : '💾 Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Import preview modal */}
      {importPreview && (
        <div onClick={e => e.target === e.currentTarget && setImportPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: T.card || '#fff', borderRadius: '16px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto', padding: '22px' }}>
            <h3 style={{ marginTop: 0, color: T.text, fontWeight: 900 }}>👁️ Aperçu de l'import</h3>
            <div style={{ fontSize: '13px', color: T.text, marginBottom: '10px' }}>
              {importPreview.records.length} ligne(s) · <b style={{ color: '#f59e0b' }}>{importPreview.dups.length} doublon(s)</b> · <b style={{ color: '#dc2626' }}>{importPreview.errors.length} erreur(s)</b>
            </div>
            <label style={{ fontSize: '12px', color: T.text, display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
              <input type="checkbox" checked={importPreview.skipDups} onChange={e => setImportPreview({ ...importPreview, skipDups: e.target.checked })} />
              Ignorer les doublons (sinon mettre à jour les existants)
            </label>
            <div style={{ maxHeight: '40vh', overflowY: 'auto', border: `1px solid ${T.cardBorder}`, borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead><tr style={{ background: dm ? '#1e293b' : '#f8fafc', color: T.textSub }}>{['#', 'Nom', 'C.N.I.E', 'Tél', 'Risque', 'État'].map(h => <th key={h} style={{ padding: '6px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {importPreview.records.slice(0, 200).map((r, i) => {
                    const dup = importPreview.dups.includes(i);
                    const err = importPreview.errors.find(e => e.row === i + 2);
                    return (
                      <tr key={i} style={{ borderTop: `1px solid ${T.cardBorder}`, color: T.text, background: err ? 'rgba(220,38,38,0.08)' : dup ? 'rgba(245,158,11,0.08)' : 'transparent' }}>
                        <td style={{ padding: '5px' }}>{i + 2}</td>
                        <td style={{ padding: '5px' }}>{fullName(r)}</td>
                        <td style={{ padding: '5px' }}>{r.cnie}</td>
                        <td style={{ padding: '5px' }}>{r.phone}</td>
                        <td style={{ padding: '5px' }}>{r.riskLevel}</td>
                        <td style={{ padding: '5px', fontWeight: 700 }}>{err ? '❌ ' + err.errs[0] : dup ? '⚠️ Doublon' : '✅ OK'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button onClick={() => setImportPreview(null)} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: dm ? '#1e293b' : '#e2e8f0', color: T.text, fontWeight: 700, cursor: 'pointer' }}>Annuler</button>
              <button onClick={runImport} disabled={busy} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>{busy ? '⏳ Import...' : `✅ Importer (${importPreview.records.length})`}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 100001, background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: 700 }}>{toast}</div>}
    </div>
  );
}
